import React, { useState, useEffect } from "react";
import { PaiementService } from "../../services/paiements/paiementService";
import { CreditCard, CheckCircle, Clock, XCircle, Download } from "lucide-react";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { exportToCSV } from "../../utils/exportUtils";

interface Paiement {
  id: string;
  reference_paiement: string;
  montant: number;
  created_at: string;
  statut: "initie" | "valide" | "execute" | "rejete" | "echoue";
  facture?: {
    numero: string;
    fournisseur_nom?: string;
  };
  beneficiaire_nom?: string;
}

export const FournisseurPaiements: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [paiements, setPaiements] = useState<Paiement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm] = useState("");

  const fetchPaiements = async () => {
    try {
      setLoading(true);
      const response = await PaiementService.getPaiements();
      setPaiements(response.data.results || []);
    } catch (error) {
      console.error("Erreur chargement paiements", error);
      toast.error(t('paiements.messages.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaiements();
  }, []);

  const filteredPaiements = paiements.filter((p) => 
    p.reference_paiement?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.facture?.numero.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount: number) => new Intl.NumberFormat(i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US', { style: "currency", currency: "XOF" }).format(amount);

  const getStatusConfig = (statut: string) => {
    switch (statut) {
      case "execute": return { color: "bg-success-100 text-success-800", icon: CheckCircle, label: t('paiements.fournisseurPages.received') };
      case "valide": return { color: "bg-primary-100 text-primary-800", icon: Clock, label: t('paiements.statuses.valide') || "Validé" };
      case "initie": return { color: "bg-warning-100 text-warning-800", icon: Clock, label: t('paiements.statuses.initie') };
      case "rejete": return { color: "bg-error-100 text-error-800", icon: XCircle, label: t('paiements.statuses.annule') };
      default: return { color: "bg-gray-100 text-gray-800", icon: Clock, label: statut };
    }
  };

  if (loading && paiements.length === 0) {
      return (
          <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
      );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('paiements.fournisseurPages.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">{t('paiements.fournisseurPages.overview')}</p>
        </div>
        <button 
             onClick={() => exportToCSV(paiements, "mes_paiements")}
             className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
             <Download className="h-4 w-4 mr-2" /> {t('common.download')}
        </button>
      </div>

      <div className="bg-white shadow rounded-lg">
        {/* Simple List */}
        <div className="divide-y divide-gray-200">
            {filteredPaiements.length === 0 ? (
                <div className="p-8 text-center text-gray-500">{t('paiements.noPaymentFound')}</div>
            ) : (
                filteredPaiements.map(paiement => {
                    const status = getStatusConfig(paiement.statut);
                    const StatusIcon = status.icon;
                    return (
                        <div key={paiement.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <div className="flex-shrink-0">
                                        <div className="p-2 bg-gray-100 rounded-lg">
                                            <CreditCard className="h-6 w-6 text-gray-600" />
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center">
                                            <span className="text-sm font-medium text-gray-900">{paiement.reference_paiement}</span>
                                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                                                <StatusIcon className="h-3 w-3 mr-1" /> {status.label}
                                            </span>
                                        </div>
                                        <div className="mt-1 text-sm text-gray-500">
                                            {t('factures.title')}: {paiement.facture?.numero || convertReferenceToInvoice(paiement.reference_paiement || "")}
                                        </div>
                                         <div className="text-xs text-gray-400">
                                            {format(new Date(paiement.created_at), "dd MMMM yyyy", { locale: i18n.language.startsWith('fr') ? fr : enUS })}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-bold text-gray-900">{formatCurrency(paiement.montant)}</div>
                                </div>
                            </div>
                        </div>
                    );
                })
            )}
        </div>
      </div>
    </div>
  );
};

// Helper for demo
const convertReferenceToInvoice = (ref: string) => ref.replace("PAY", "FAC");
